// AI Chat Types

import type { BotTag, TaskApiPayload, WorkflowLinks } from '../chatbot/types';

/**
 * guide is Ask mode: answers from the workflow guide, with citations. solution is Solve
 * mode: a configuration plan for a client requirement.
 */
export type SessionType = 'workflow_create' | 'workflow_modify' | 'guide' | 'solution';
export type SessionStatus = 'active' | 'completed' | 'abandoned';
export type MessageRole = 'user' | 'assistant' | 'system';

/** A document attached to a Solve session, as the Solve engine reads it */
export interface AIChatDocument {
  id: string;
  session_id: string;
  filename: string;
  content_type: string;
  markdown: string;
  /** Only the first part of a long document was kept */
  truncated: boolean;
  created_at: string;
}

export interface AIChatSession {
  id: string;
  org_id: string;
  user_id: string;
  session_type: SessionType;
  session_status: SessionStatus;
  target_chatbot_id?: string;
  created_at: string;
  updated_at: string;
  /** The first user message, cut to 120 characters; only the session list sets it */
  preview?: string;
  /** A turn is running on the session, so its reply is still to come */
  turn_in_progress?: boolean;
}

export interface AIChatMessage {
  id: string;
  session_id: string;
  role: MessageRole;
  content: string;
  metadata: AIChatMessageMetadata;
  tokens_used: number;
  created_at: string;
}

export interface AIChatMessageMetadata {
  questions?: AIClarifyingQuestion[];
  assumptions?: string[];
  model?: string;
  feedback?: MessageFeedback;
  feedback_comment?: string;
  /** Draft sessions: the draft's lint result after the turn */
  lint_summary?: AIDraftLintSummary;
  /** Guide entry ids an Ask answer cites; the content writes them as [[entry-id]] */
  citations?: string[];
  /** The guide does not answer the question */
  not_covered?: boolean;
  tool_steps?: number;
  /** Solution sessions: the plan; the content is the same plan as Markdown */
  plan?: AISolutionPlan;
  /** Solution sessions: one Build request per plan workflow, in order */
  build_briefs?: string[];
  /** Solution sessions: grounding problems the plan still has */
  plan_issues?: string[];
}

/** A configuration plan from Solve mode (ai/solve.Plan) */
export interface AISolutionPlan {
  summary: string;
  datasets: { name: string; exists: boolean; row: string; belongs_to: string; purpose: string }[];
  workflows: { title: string; template: 'sop' | 'form'; flows: boolean }[];
  gaps: { need: string; impact: string; workaround: string; uncovered: boolean }[];
  open_questions: string[];
}

export type MessageFeedback = 'up' | 'down';

export interface SendAIChatFeedbackRequest {
  feedback: MessageFeedback;
  comment?: string;
}

export interface AIClarifyingQuestion {
  text: string;
  options?: string[];
  allow_free_text?: boolean;
}

export interface AIChatSessionWithMessages {
  session: AIChatSession;
  messages: AIChatMessage[];
}

// Request/Response DTOs

export interface CreateAIChatSessionRequest {
  session_type: SessionType;
  /** The workflow a workflow_modify session edits */
  target_chatbot_id?: string;
}

/** A draft's lint result after a Build turn */
export interface AIDraftLintSummary {
  status: AIDraftStatus;
  errors: number;
  blockers: number;
  confirms: number;
}

/** ready can open in the editor; the others say what stops it */
export type AIDraftStatus = 'ready' | 'needs_confirmation' | 'blocked' | 'invalid' | '';

/** error: the assistant fixes it. blocker: the user creates something. confirm: the user confirms it. */
export type AIDraftSeverity = 'error' | 'blocker' | 'confirm';

export interface AIDraftIssue {
  severity: AIDraftSeverity;
  code: string;
  task?: string;
  field?: string;
  message: string;
  guide?: string;
  /** App page where the user fixes a blocker */
  link?: string;
  /** The dataset a dataset blocker is about */
  dataset?: string;
  /** Import columns a missing dataset needs */
  columns?: string[];
  confirm_id?: string;
  confirmed?: boolean;
}

/** A task as the draft stores it: its TaskItem fields under fields, keyed */
export interface AIDraftTask {
  key: string;
  id: string;
  fields: Record<string, unknown> & { name?: string; type?: string; actions?: string[] };
}

export interface AIDraft {
  mode: 'create' | 'modify';
  source_chatbot_id?: string;
  workflow: { title: string; template: string; task_lifecycle?: string; ttl: number; ttl_unit?: string };
  tasks: AIDraftTask[];
  assumptions?: string[];
  confirmed?: string[];
}

export interface AIDraftView {
  mode?: 'create' | 'modify';
  status: AIDraftStatus;
  draft?: AIDraft;
  issues: AIDraftIssue[];
  assumptions?: string[];
}

/** The request that saves a ready draft: create for a new workflow, update and links for an edit */
export interface AIDraftExport {
  mode: 'create' | 'modify';
  chatbot_id?: string;
  create?: CreateBotRequestPayload;
  update?: CreateBotRequestPayload;
  links?: WorkflowLinks;
}

/** CreateBotRequestV2 / UpdateBotRequestV2 as the export returns them */
export interface CreateBotRequestPayload {
  title: string;
  template: string;
  ttl: number;
  ttl_unit?: 'hours' | 'days';
  task_lifecycle?: string;
  tags?: BotTag[];
  tasks: TaskApiPayload[];
  links?: WorkflowLinks;
}

/** Export either succeeds, or reports why the draft is not ready */
export type AIDraftExportResult = { ready: true; export: AIDraftExport } | { ready: false; view: AIDraftView };

export interface SendAIChatMessageRequest {
  content: string;
}

export interface AIChatMessageResponse {
  message: AIChatMessage;
}

// Streaming (SSE) send

export type AIStreamStatus = 'validating' | 'repairing';

export interface AIChatStreamHandlers {
  /** Called for each assistant text token as it is generated */
  onToken?: (token: string) => void;
  /** Called when the turn enters validation or repair of a proposed workflow */
  onStatus?: (status: AIStreamStatus) => void;
  /** Called with a guide session's progress text, e.g. "Reading guide: Linked workflows" */
  onProgress?: (text: string) => void;
}

// Chatbot type from existing API (simplified for preview)
export interface Chatbot {
  id: string;
  org_id: string;
  title: string;
  short_code: string;
  template: 'sop' | 'form';
  ttl: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Error types for rate limiting and quotas
export type AIErrorCode =
  | 'rate_limit_user'
  | 'rate_limit_org'
  | 'rate_limit_llm'
  | 'quota_exceeded'
  | 'quota_tokens'
  | 'quota_messages'
  | 'llm_unavailable'
  | 'llm_timeout'
  | 'llm_invalid_request'
  | 'llm_auth_error'
  | 'session_limit'
  | 'turn_in_progress'
  | 'session_not_found'
  | 'session_inactive'
  | 'internal_error'
  | 'validation_error';

export interface AIErrorResponse {
  code: number;
  error_code: AIErrorCode;
  message: string;
  retry_after: number;
  retryable: boolean;
}

export interface AIQuotaStatus {
  tokens_used: number;
  tokens_limit: number;
  tokens_remaining: number;
  tokens_percent: number;
  messages_used: number;
  messages_limit: number;
  messages_remaining: number;
  messages_percent: number;
  period_start: string;
  period_end: string;
  quota_exceeded: boolean;
  exceeded_type?: 'tokens' | 'messages';
}

// Helper to check if an error is an AI-specific error
export function isAIError(error: unknown): error is { response?: { data?: { data?: AIErrorResponse } } } {
  if (typeof error !== 'object' || error === null) return false;
  const err = error as Record<string, unknown>;
  if (typeof err.response !== 'object' || err.response === null) return false;
  const response = err.response as Record<string, unknown>;
  if (typeof response.data !== 'object' || response.data === null) return false;
  const data = response.data as Record<string, unknown>;
  return typeof data.data === 'object' && data.data !== null && 'error_code' in (data.data as Record<string, unknown>);
}

// Extract AI error details from an API error response
export function extractAIError(error: unknown): AIErrorResponse | null {
  if (!isAIError(error)) return null;
  return (error.response?.data?.data as AIErrorResponse) ?? null;
}

/** The chat's session is over, so the next message has to start a new one */
export function isChatEndedError(error: unknown): boolean {
  const code = extractAIError(error)?.error_code;
  return code === 'session_inactive' || code === 'session_not_found';
}
